import { type FormStructure, type BookingStatus } from "./types";
import { type SelectOption } from "@/components/SelectInput";

export const bookingStatusOptions: SelectOption[] = [
  { label: "Draft", value: "DRAFT" },
  { label: "Planned", value: "PLANNED" },
  { label: "Booked", value: "BOOKED" },
  { label: "Shipped", value: "SHIPPED" },
  { label: "Awaiting Payment", value: "AWAITING_PAYMENT" },
  { label: "Paid", value: "PAID" },
  { label: "Archived", value: "ARCHIVED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Closed", value: "CLOSED" },
] satisfies Array<{ label: string; value: BookingStatus }>;

export const CONDITION_OPTIONS = [
  { value: "THIN", label: "Thin" },
  { value: "MEDIUM_LIGHT", label: "Medium Light" },
  { value: "MEDIUM", label: "Medium" },
  { value: "MEDIUM_PLUS", label: "Medium Plus" },
  { value: "FLESHY", label: "Fleshy" },
  { value: "UNWEANED", label: "Unweaned" },
  { value: "WEANED", label: "Weaned" },
  { value: "YEARLING", label: "Yearling" },
  { value: "CALF", label: "Calf" },
] as const;

export type Condition = (typeof CONDITION_OPTIONS)[number]["value"];

export const OWNERSHIP_TYPE_OPTIONS = [
  { value: "COMPANY", label: "Company" },
  { value: "CUSTOMER", label: "Customer" },
  { value: "PARTNERSHIP", label: "Partnership" },
] as const;

export type OwnershipType = (typeof OWNERSHIP_TYPE_OPTIONS)[number]["value"];

export const formStructure: FormStructure = [
  {
    id: "lot_inventory",
    title: "Lot & Inventory Details",
    fields: [
      { id: "LOT_NUMBER", section: "lot_inventory" },
      { id: "PEN_NUMBER", section: "lot_inventory" },
      { id: "ACCOUNT_NUMBER", section: "lot_inventory" },
      { id: "SEX_CLASS", section: "lot_inventory" },
      { id: "ACCREDITATIONS", section: "lot_inventory" },
      { id: "LABELS", section: "lot_inventory" },
      { id: "CONFIRMED", section: "lot_inventory" },
      { id: "OWNERSHIP_TYPE", section: "lot_inventory" },
      { id: "BREED_NOTE", section: "lot_inventory" },
    ],
  },
  {
    id: "dates",
    title: "Dates & Scheduling",
    fields: [
      { id: "DATE_OF_SALE", section: "dates" },
      { id: "DATE_OF_VISIT", section: "dates" },
      { id: "DATE_DELIVERED", section: "dates" },
      { id: "PROJECTED_OUT_DATE", section: "dates" },
    ],
  },
  {
    id: "financial",
    title: "Financial Details",
    fields: [
      { id: "PRICE", section: "financial" },
      { id: "FREIGHT_COST", section: "financial" },
      { id: "SLIDE", section: "financial" },
      { id: "PURCHASE_PRICE_PER_CWT", section: "financial" },
      { id: "RISK_MANAGEMENT", section: "financial" },
    ],
  },
  {
    id: "weight",
    title: "Weight & Quality Information",
    fields: [
      { id: "SHIPPED_WEIGHT", section: "weight" },
      { id: "BASE_WEIGHT", section: "weight" },
      { id: "ESTIMATED_CHOICE_PERCENT", section: "weight" },
      { id: "SHRINK_PERCENT", section: "weight" },
      { id: "CONDITION", section: "weight" },
      { id: "CONDITIONS", section: "lot_inventory" },
      { id: "ESTIMATED_WEIGHT_NOTE", section: "weight", columnSpan: 3 },
    ],
  },
  {
    id: "source",
    title: "Source & Documentation",
    fields: [
      { id: "CERTIFICATE_NUMBER", section: "source" },
      { id: "PROGRAM", section: "source" },
      { id: "GRID", section: "source" },
      { id: "SOURCE_NOTE", section: "source" },
      { id: "BRAND_NOTE", section: "source" },
    ],
  },
  {
    id: "transportation",
    title: "Transportation & Services",
    fields: [
      { id: "TRUCKING_COMPANY", section: "transportation" },
      { id: "BROKER", section: "transportation" },
      { id: "VETERINARIAN", section: "transportation" },
      { id: "SERVICE_PROVIDED", section: "transportation", columnSpan: 3 },
    ],
  },
] as const;
